import {clearPendingTour, registerTourSteps, setPendingTour} from './product-tours.js'

// Walk-through of "Call using my cell phone" across the pages a nurse really uses: any page,
// then a patient's chart (green Call button), then the call window (the "My cell phone" choice).
// Launched through the shared registry launcher in product-tours.js: from the tours drawer, or from
// the `#cpm-tour=nurse_cell_phone_calling` link in the release-announcement Messenger message (see
// Modules/Bubble/config/release-announcements.php). Each stage that hands over to the next page
// leaves a pending note (setPendingTour), so the tour picks itself up there without another click.
export const NURSE_CELL_CALL_TOUR_ID = 'nurse_cell_phone_calling'
const callButtonSelector = '[data-tour="call-button"]'
const switchSelector = '[data-tour="nurse-cell-mode-switch"]'
const optionSelector = '[data-tour="nurse-cell-mode-option"]'

export function nurseCellCallTourSteps(documentObject, browserWindow = window) {
    const startedFromLink = /cpm-tour=/.test(browserWindow.location?.hash ?? '')

    // Call window: pick "My cell phone", set it up once, then call as usual.
    if (documentObject.querySelector(switchSelector)) {
        return {
            steps: [
                {
                    selector: optionSelector,
                    position: 'bottom',
                    title: 'Tap "My cell phone"',
                    text: 'This is the new option. Tap it now. It is optional: "This computer" still works the same as before.',
                    advanceOn: {selector: optionSelector, event: 'click'},
                },
                {
                    selector: switchSelector,
                    position: 'bottom',
                    title: 'Set it up once',
                    text: 'Type your cell number and tap "Call me to test it". Answer the call and press 1. You only do this the first time.',
                },
                {
                    selector: switchSelector,
                    position: 'bottom',
                    title: 'Then call as usual',
                    text: 'Click the green button next to the patient\'s number. CPM rings your cell first. Answer, press 1, and we connect the patient. The patient only sees the practice number, never yours.',
                },
            ],
            onEnd: () => clearPendingTour(browserWindow),
        }
    }

    // Patient page: point at the green Call button, which opens the call window.
    if (documentObject.querySelector(callButtonSelector)) {
        setPendingTour(NURSE_CELL_CALL_TOUR_ID, browserWindow)

        return {
            steps: [
                {
                    selector: callButtonSelector,
                    position: 'bottom',
                    title: 'Click the green Call button',
                    text: 'Click it now. A small call window opens, and that is where the new option is.',
                    advanceOn: {selector: callButtonSelector, event: 'click'},
                },
                {
                    selector: null,
                    title: 'Look for the call window',
                    text: 'The tour continues in the call window that just opened. If you do not see it, check behind this window.',
                },
            ],
            onEnd: null,
            onCancel: () => clearPendingTour(browserWindow),
        }
    }

    // Anywhere else: send them to a patient and pick the tour up there. A resumed tour stays quiet.
    if (!startedFromLink) {
        return {steps: [], onEnd: null}
    }

    return {
        steps: [{
            selector: null,
            title: 'Open any patient first',
            text: 'Open any patient from your list. This tour will start again there and show you the green Call button.',
        }],
        deferred: true,
        onDefer: () => setPendingTour(NURSE_CELL_CALL_TOUR_ID, browserWindow),
        onCancel: () => clearPendingTour(browserWindow),
        onEnd: null,
    }
}

export function registerNurseCellCallTour() {
    registerTourSteps(NURSE_CELL_CALL_TOUR_ID, nurseCellCallTourSteps)
}
